import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, UploadCloud, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const VISA_OPTIONS = [
  "Student visa (Tier 4) — currently studying",
  "Student visa — graduating soon",
  "Graduate visa (post-study, 2 years)",
  "Skilled Worker visa — sponsored",
  "Requires Skilled Worker sponsorship",
  "British / Irish citizen",
  "Other / Prefer to discuss",
];

export default function Intake() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [orders, setOrders] = useState(null);
  const [orderId, setOrderId] = useState(params.get("order_id") || "");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadedName, setUploadedName] = useState("");
  const [form, setForm] = useState({ target_industry: "", target_roles: "", visa_status: "", achievements: "", notes: "" });

  useEffect(() => {
    if (user === false) return navigate("/auth?next=/intake");
    if (!user) return;
    api.get("/orders/my").then((r) => {
      const eligible = r.data.filter((o) => o.status !== "awaiting_payment");
      setOrders(eligible);
      const sessionId = params.get("session_id");
      const bySession = r.data.find((o) => o.session_id === sessionId);
      if (bySession) setOrderId(bySession.id);
      else if (eligible.length > 0 && !orderId) setOrderId(eligible[0].id);
    }).catch(() => setOrders([]));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selectedOrder = useMemo(() => (orders || []).find((o) => o.id === orderId), [orders, orderId]);

  const uploadFile = async () => {
    if (!file || !orderId) return;
    const data = new FormData();
    data.append("order_id", orderId);
    data.append("file", file);
    try {
      const res = await api.post("/intake/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadedName(res.data.filename);
      toast.success("CV uploaded securely");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/intake", { order_id: orderId, ...form });
      setDone(true);
      toast.success("Questionnaire submitted — our HR desk has your brief.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (user === null || orders === null) {
    return <div className="mx-auto max-w-3xl px-5 py-32 text-sm text-slate-500" data-testid="intake-loading">Loading your orders…</div>;
  }

  if (orders.length === 0) {
    return (
      <div className="mx-auto max-w-xl px-5 py-32 text-center" data-testid="intake-no-orders">
        <h1 className="font-serif text-3xl font-semibold text-slate-50">No paid orders yet</h1>
        <p className="mt-3 text-sm text-slate-400">Once your payment is confirmed, your career questionnaire unlocks here.</p>
        <button onClick={() => navigate("/order")} className="btn-gold mt-8" data-testid="intake-browse-packages-btn">Browse Packages</button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-5 py-24 text-center" data-testid="intake-complete">
        <CheckCircle2 size={52} className="text-emerald-400" />
        <h1 className="mt-8 font-serif text-4xl font-semibold text-slate-50">Brief received</h1>
        <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
          A UK HR professional is now reviewing your career history. Track progress live in your client portal — first draft within your package's turnaround window.
        </p>
        <button onClick={() => navigate("/dashboard")} className="btn-gold mt-10" data-testid="intake-goto-dashboard-btn">Track My Order</button>
      </div>
    );
  }

  const steps = [
    { n: 1, label: "Target Role" },
    { n: 2, label: "Upload CV" },
    { n: 3, label: "Career Detail" },
    { n: 4, label: "Review" },
  ];

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24 pt-16 lg:pt-24" data-testid="intake-page">
      <span className="overline-label">Career Questionnaire</span>
      <h1 className="mt-4 font-serif text-4xl font-semibold text-slate-50 sm:text-5xl" data-testid="intake-headline">
        <MaskedLine delay={0.1}>Brief your</MaskedLine>
        <MaskedLine delay={0.3} className="italic text-[#C5A059]">HR Executive.</MaskedLine>
      </h1>

      {/* ORDER SELECT */}
      {orders.length > 1 && (
        <div className="mt-8">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Which order is this for?</label>
          <select value={orderId} onChange={(e) => setOrderId(e.target.value)} className="input-dark" data-testid="intake-order-select">
            {orders.map((o) => (
              <option key={o.id} value={o.id}>{o.package_name} — £{Number(o.amount).toFixed(2)} ({new Date(o.created_at).toLocaleDateString("en-GB")})</option>
            ))}
          </select>
        </div>
      )}

      {/* STEPPER */}
      <div className="mt-10 flex items-center gap-2" data-testid="intake-stepper">
        {steps.map((s, i) => (
          <div key={s.n} className="flex flex-1 items-center gap-2">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border font-mono text-xs font-semibold transition-colors duration-300 ${step >= s.n ? "border-[#C5A059] bg-[#C5A059] text-[#0A0F1D]" : "border-slate-700 text-slate-500"}`}>
              {s.n}
            </div>
            <span className={`hidden text-xs font-medium sm:block ${step >= s.n ? "text-slate-200" : "text-slate-600"}`}>{s.label}</span>
            {i < steps.length - 1 && <div className={`h-px flex-1 ${step > s.n ? "bg-[#C5A059]" : "bg-slate-800"}`} />}
          </div>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="card-surface mt-8 p-8 lg:p-10">
          {step === 1 && (
            <div className="space-y-6" data-testid="intake-step-1">
              <h2 className="font-serif text-2xl font-semibold text-slate-50">Where are you headed in the UK?</h2>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="intake-industry">Target UK Industry *</label>
                <input id="intake-industry" data-testid="intake-industry-input" value={form.target_industry} onChange={set("target_industry")} className="input-dark" placeholder="e.g. FinTech, NHS, Civil Service, Consulting" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="intake-roles">Specific Target Roles *</label>
                <input id="intake-roles" data-testid="intake-roles-input" value={form.target_roles} onChange={set("target_roles")} className="input-dark" placeholder="e.g. Business Analyst, Staff Nurse, Backend Engineer" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="intake-visa">Right-to-Work / Visa Status *</label>
                <select id="intake-visa" data-testid="intake-visa-select" value={form.visa_status} onChange={set("visa_status")} className="input-dark">
                  <option value="">Select your status…</option>
                  {VISA_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="intake-step-2">
              <h2 className="font-serif text-2xl font-semibold text-slate-50">Upload your existing CV</h2>
              <p className="text-sm text-slate-400">PDF or Word, up to 10MB. Don't have one? Skip ahead and describe your history in the next step.</p>
              <label
                htmlFor="intake-cv-upload-input"
                className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-700 px-6 py-14 transition-colors duration-200 hover:border-[#C5A059]/60"
                data-testid="intake-cv-dropzone"
              >
                <UploadCloud size={32} className="text-[#C5A059]" />
                <span className="mt-4 text-sm font-medium text-slate-200">{file ? file.name : "Click to choose your CV file"}</span>
                <span className="mt-1 font-mono text-xs text-slate-500">PDF · DOC · DOCX · max 10MB</span>
                <input id="intake-cv-upload-input" data-testid="intake-cv-upload-input" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => { setFile(e.target.files[0] || null); setUploadedName(""); }} />
              </label>
              {file && !uploadedName && (
                <button onClick={uploadFile} className="btn-ghost" data-testid="intake-upload-btn">Upload {file.name}</button>
              )}
              {uploadedName && (
                <p className="inline-flex items-center gap-2 text-sm text-emerald-400" data-testid="intake-upload-success"><CheckCircle2 size={16} /> {uploadedName} uploaded</p>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="intake-step-3">
              <h2 className="font-serif text-2xl font-semibold text-slate-50">Your career, in your words</h2>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="intake-achievements">Key Achievements & Experience *</label>
                <textarea id="intake-achievements" data-testid="intake-achievements-input" rows={7} value={form.achievements} onChange={set("achievements")} className="input-dark resize-none" placeholder="Your degree, university and graduation date, plus any internships, part-time work, projects or achievements with numbers where possible (e.g. 'led a 12-person university project', 'top 5% of cohort'). Our HR team will reshape this into British corporate language." />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="intake-notes">Anything else we should know?</label>
                <textarea id="intake-notes" data-testid="intake-notes-input" rows={4} value={form.notes} onChange={set("notes")} className="input-dark resize-none" placeholder="Career gaps, job offers in progress, specific companies you're targeting, preferred review times (GMT)…" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6" data-testid="intake-step-4">
              <h2 className="font-serif text-2xl font-semibold text-slate-50">Review your brief</h2>
              <dl className="space-y-4 rounded-xl border border-slate-700/50 p-6 text-sm">
                <div className="flex justify-between gap-6"><dt className="text-slate-500">Order</dt><dd className="text-right text-slate-200">{selectedOrder?.package_name}</dd></div>
                <div className="flex justify-between gap-6"><dt className="text-slate-500">Industry</dt><dd className="text-right text-slate-200">{form.target_industry || "—"}</dd></div>
                <div className="flex justify-between gap-6"><dt className="text-slate-500">Target roles</dt><dd className="text-right text-slate-200">{form.target_roles || "—"}</dd></div>
                <div className="flex justify-between gap-6"><dt className="text-slate-500">Visa status</dt><dd className="text-right text-slate-200">{form.visa_status || "—"}</dd></div>
                <div className="flex justify-between gap-6"><dt className="text-slate-500">CV uploaded</dt><dd className="text-right text-slate-200">{uploadedName || "None"}</dd></div>
              </dl>
              <p className="text-xs leading-relaxed text-slate-500">
                By submitting, you confirm the details are accurate. You can message revisions through your client portal at any time.
              </p>
            </div>
          )}

          {/* NAV */}
          <div className="mt-10 flex items-center justify-between">
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="btn-ghost disabled:invisible"
              data-testid="intake-wizard-back-btn"
            >
              <ArrowLeft size={15} /> Back
            </button>
            {step < 4 ? (
              <button
                onClick={() => setStep((s) => Math.min(4, s + 1))}
                disabled={step === 1 && (!form.target_industry || !form.target_roles || !form.visa_status)}
                className="btn-gold disabled:opacity-50"
                data-testid="intake-wizard-next-btn"
              >
                Continue <ArrowRight size={15} />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={busy || !form.achievements}
                className="btn-gold disabled:opacity-50"
                data-testid="intake-submit-btn"
              >
                {busy ? "Submitting…" : "Submit Brief"} <ArrowRight size={15} />
              </button>
            )}
          </div>
        </div>
      </Reveal>
    </div>
  );
}
